import React from 'react';
import { useWatch } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Check, Loader2, Lock, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { fetchCustomSlugAvailability } from '@/services/entry-point';
import { useAuthenticationState } from '@/hooks/use-authentication';
import { cn } from '@/lib/utils';
/** Mirrors `App\Util\CustomSlug::MIN_LENGTH`. Below this the probe is pointless. */
const MIN_SLUG_LENGTH = 3;
/**
 * Vanity-URL input for a campaign's default smart link. The value is a bare
 * slug (`coffee-promo`); the `/c/` prefix is chrome, not part of the field.
 *
 * The slug namespace is platform-wide, so a collision can only be settled by
 * the server. The debounced probe exists to surface that verdict while the
 * user is still typing rather than at submit, when they've already moved on.
 * It is an affordance, never the gate — the create and patch endpoints
 * re-validate, re-check the plan and re-check uniqueness on write.
 */
function CustomSlugFieldInner({ control, name, label, disabled, excludeEntryPointId, className, }) {
    const { t } = useTranslation();
    const { workspace } = useAuthenticationState();
    const allowCustomSlug = Boolean(workspace?.subscription?.plan?.allowCustomSlug);
    const raw = useWatch({ control, name });
    const current = typeof raw === 'string' ? raw.trim() : '';
    const debounced = useDebouncedValue(current, 400);
    const { data: verdict, isFetching } = useQuery({
        queryKey: ['entry-point-slug-availability', debounced, excludeEntryPointId],
        queryFn: () => fetchCustomSlugAvailability({ slug: debounced, exclude: excludeEntryPointId }),
        enabled: allowCustomSlug && debounced.length >= MIN_SLUG_LENGTH,
        staleTime: 30_000,
    });
    // Only trust a verdict that describes what's in the box right now — a
    // stale "available" left over from the previous keystroke would be a
    // green tick under a slug nobody checked.
    const settled = !isFetching && debounced === current && current.length >= MIN_SLUG_LENGTH ? verdict : undefined;
    const origin = typeof window === 'undefined' ? '' : window.location.origin.replace(/^https?:\/\//, '');
    if (!allowCustomSlug) {
        return (<div className={cn('space-y-2', className)}>
                {label && <p className="text-sm font-medium leading-none">{label}</p>}
                <div className="flex items-center gap-2 rounded-md border border-dashed border-input bg-muted/30 px-3 py-2.5">
                    <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground"/>
                    <p className="text-xs text-muted-foreground">
                        {t('campaigns.custom_slug.locked', {
                ns: 'glossary',
                defaultValue: 'Custom URLs are a Solo+ feature.',
            })}{' '}
                        <Link to="/console/workspace/billing" className="text-primary underline underline-offset-2">
                            {t('upgrade', { defaultValue: 'Upgrade' })}
                        </Link>
                    </p>
                </div>
            </div>);
    }
    return (<FormField control={control} name={name} render={({ field }) => (<FormItem className={cn('space-y-2', className)}>
                    {label && <FormLabel htmlFor={name}>{label}</FormLabel>}
                    <FormControl>
                        <div className="flex items-stretch">
                            <span className="inline-flex select-none items-center rounded-l-md border border-r-0 border-input bg-muted/50 px-3 text-xs text-muted-foreground">
                                {origin}/c/
                            </span>
                            <div className="relative flex-1">
                                <Input {...field} value={typeof field.value === 'string' ? field.value : ''} 
        // Normalising here keeps the preview honest:
        // the server lowercases anyway, so showing
        // the user anything else is a small lie.
        onChange={(event) => field.onChange(event.target.value.toLowerCase())} id={name} name={name} disabled={disabled} placeholder={t('campaigns.custom_slug.placeholder', {
                ns: 'glossary',
                defaultValue: 'coffee-promo',
            })} className="rounded-l-none pr-9 font-mono text-sm" autoComplete="off" spellCheck={false}/>
                                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                    {isFetching && current.length >= MIN_SLUG_LENGTH ? (<Loader2 className="h-4 w-4 animate-spin text-muted-foreground"/>) : settled?.available ? (<Check className="h-4 w-4 text-emerald-500"/>) : settled ? (<X className="h-4 w-4 text-destructive"/>) : null}
                                </span>
                            </div>
                        </div>
                    </FormControl>
                    <FormMessage />
                    {settled && !settled.available && settled.message ? (<p className="text-xs text-destructive">{settled.message}</p>) : (<FormDescription className="text-xs">
                            {t('campaigns.custom_slug.help', {
                    ns: 'glossary',
                    defaultValue: 'Optional. Lowercase letters, digits and hyphens. Leave empty to keep the auto-generated link.',
                })}
                        </FormDescription>)}
                </FormItem>)}/>);
}
/**
 * Trailing-edge debounce. Keeps the availability probe off every keystroke
 * without pulling in a dependency for four lines of `setTimeout`.
 */
function useDebouncedValue(value, delayMs) {
    const [debounced, setDebounced] = React.useState(value);
    React.useEffect(() => {
        const timer = window.setTimeout(() => setDebounced(value), delayMs);
        return () => window.clearTimeout(timer);
    }, [value, delayMs]);
    return debounced;
}
CustomSlugFieldInner.displayName = 'CustomSlugField';
export const CustomSlugField = React.memo(CustomSlugFieldInner);
